const BLOCK_CLASS_NAME = "animate-pulse bg-neutral-200 motion-reduce:animate-none dark:bg-neutral-800";

export function SkeletonBlock({ className = "" }) {
  return <div aria-hidden="true" className={`${BLOCK_CLASS_NAME} ${className}`} />;
}

export function SkeletonRegion({ label = "Loading content", className = "", children }) {
  return (
    <div
      className={className}
      role="status"
      aria-busy="true"
      aria-live="polite"
      aria-label={label}
    >
      <span className="sr-only">{label}</span>
      {children}
    </div>
  );
}

export function ProductCardSkeleton({ compact = false }) {
  return (
    <div className={compact ? "min-w-0" : "rounded-2xl border border-neutral-200 p-2.5 dark:border-neutral-800 sm:p-3"}>
      <SkeletonBlock className="aspect-[4/5] w-full rounded-xl" />
      <SkeletonBlock className="mt-3 h-3.5 w-4/5 rounded" />
      <SkeletonBlock className="mt-2 h-3.5 w-2/5 rounded" />
      {!compact ? <SkeletonBlock className="mt-3 h-9 w-full rounded-xl sm:mt-4 sm:h-10" /> : null}
    </div>
  );
}

export function ProductGridSkeleton({
  count = 8,
  className = "grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 lg:grid-cols-4",
  compact = false,
  label = "Loading products",
}) {
  return (
    <SkeletonRegion label={label} className={className}>
      {Array.from({ length: count }, (_, index) => (
        <ProductCardSkeleton key={`product-skeleton-${index}`} compact={compact} />
      ))}
    </SkeletonRegion>
  );
}

export function ProductRowSkeleton({ count = 5, label = "Loading products" }) {
  return (
    <SkeletonRegion label={label} className="flex w-full gap-4 overflow-hidden pb-3">
      {Array.from({ length: count }, (_, index) => (
        <div key={`product-row-skeleton-${index}`} className="w-40 shrink-0 sm:w-48 lg:w-52">
          <SkeletonBlock className="aspect-square rounded-xl" />
          <SkeletonBlock className="mt-3 h-3.5 w-4/5 rounded" />
          <SkeletonBlock className="mt-2 h-3.5 w-2/5 rounded" />
        </div>
      ))}
    </SkeletonRegion>
  );
}

export function DetailPageSkeleton({ label = "Loading details", showMedia = true }) {
  return (
    <SkeletonRegion
      label={label}
      className="mx-auto grid w-full max-w-6xl gap-6 px-4 py-6 md:grid-cols-2 md:gap-8 sm:px-6"
    >
      {showMedia ? <SkeletonBlock className="aspect-square w-full rounded-2xl" /> : null}
      <div className="space-y-5">
        <SkeletonBlock className="h-4 w-28 rounded" />
        <SkeletonBlock className="h-8 w-4/5 rounded-lg" />
        <SkeletonBlock className="h-5 w-2/5 rounded" />
        <SkeletonBlock className="h-20 w-full rounded-2xl" />
        <div className="space-y-3 rounded-2xl border border-neutral-200 p-4 dark:border-neutral-800">
          <SkeletonBlock className="h-4 w-1/3 rounded" />
          <SkeletonBlock className="h-11 w-full rounded-xl" />
          <SkeletonBlock className="h-11 w-full rounded-xl" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <SkeletonBlock className="h-12 rounded-xl" />
          <SkeletonBlock className="h-12 rounded-xl" />
        </div>
      </div>
    </SkeletonRegion>
  );
}

export function ListSkeleton({ rows = 4, label = "Loading list", className = "space-y-3" }) {
  return (
    <SkeletonRegion label={label} className={className}>
      {Array.from({ length: rows }, (_, index) => (
        <div
          key={`list-skeleton-${index}`}
          className="flex items-center gap-3 rounded-2xl border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-900"
        >
          <SkeletonBlock className="h-12 w-12 shrink-0 rounded-xl" />
          <div className="min-w-0 flex-1">
            <SkeletonBlock className="h-3.5 w-3/5 rounded" />
            <SkeletonBlock className="mt-2 h-3 w-4/5 rounded" />
          </div>
          <SkeletonBlock className="h-7 w-16 shrink-0 rounded-full" />
        </div>
      ))}
    </SkeletonRegion>
  );
}

export function FormSkeleton({ sections = 3, label = "Loading form" }) {
  return (
    <SkeletonRegion label={label} className="space-y-4">
      {Array.from({ length: sections }, (_, index) => (
        <section
          key={`form-skeleton-${index}`}
          className="rounded-2xl border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-900 sm:p-5"
        >
          <SkeletonBlock className="h-4 w-36 rounded" />
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <SkeletonBlock className="h-11 rounded-xl" />
            <SkeletonBlock className="h-11 rounded-xl" />
          </div>
          <SkeletonBlock className="mt-3 h-11 w-full rounded-xl" />
        </section>
      ))}
    </SkeletonRegion>
  );
}

export function ModalProductGridSkeleton({ count = 4, label = "Loading modal products" }) {
  return (
    <ProductGridSkeleton
      count={count}
      compact
      label={label}
      className="grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4"
    />
  );
}

export function ModalFormSkeleton({ label = "Loading modal content" }) {
  return (
    <SkeletonRegion label={label} className="space-y-4 py-1">
      <div className="flex items-center gap-3 border-b border-neutral-200 pb-4 dark:border-neutral-800">
        <SkeletonBlock className="h-11 w-11 shrink-0 rounded-xl" />
        <div className="min-w-0 flex-1">
          <SkeletonBlock className="h-3.5 w-3/5 rounded" />
          <SkeletonBlock className="mt-2 h-3 w-2/5 rounded" />
        </div>
      </div>
      <SkeletonBlock className="h-3.5 w-28 rounded" />
      <div className="flex gap-2">
        <SkeletonBlock className="h-9 w-20 rounded-full" />
        <SkeletonBlock className="h-9 w-20 rounded-full" />
        <SkeletonBlock className="h-9 w-20 rounded-full" />
      </div>
      <SkeletonBlock className="h-11 w-full rounded-full" />
    </SkeletonRegion>
  );
}
